// Form types and constants for song order forms
import type { UploadedImage } from "@/components/image-upload";

export type PlanType = "essentials" | "premium";

export interface FormData {
  // Plan selection
  planType: PlanType;
  
  // Step 1: Occasion
  occasion: string;
  occasionOther: string;
  
  // Step 2 (Premium only): Date for video
  includeDateInVideo: "yes" | "no" | "";
  videoDate: string;
  uploadPhotosNow: "now" | "later" | "";
  
  // Step 3: Who is the song for
  recipient: string;
  recipientOther: string;
  
  // Step 4: How many names
  nameCount: string;
  
  // Step 5: Names to include
  names: string;
  
  // Step 6: Name pronunciation
  pronunciationRecording: Blob | null;
  pronunciationRecordingUrl: string;
  
  // Step 7: Overall feeling
  feeling: string;
  feelingOther: string;
  
  // Step 8: Song style
  songStyle: string;
  songStyleOther: string;
  
  // Step 9: Tempo
  tempo: string;
  tempoOther: string;
  
  // Step 10: Language
  language: string;
  
  // Singer gender
  singerGender: string;
  
  // Step 11: Story
  story: string;
  
  // Email capture
  email: string;
  
  // Premium: Photos
  photos: File[];
  photoUrls: string[];
  uploadedImages: UploadedImage[];
  uploadSessionId: string;
  
  // Rush delivery
  rushDelivery: boolean;
}

export const initialFormData: FormData = {
  planType: "essentials",
  occasion: "",
  occasionOther: "",
  includeDateInVideo: "",
  videoDate: "",
  uploadPhotosNow: "",
  recipient: "",
  recipientOther: "",
  nameCount: "",
  names: "",
  pronunciationRecording: null,
  pronunciationRecordingUrl: "",
  feeling: "",
  feelingOther: "",
  songStyle: "",
  songStyleOther: "",
  tempo: "",
  tempoOther: "",
  language: "",
  singerGender: "",
  story: "",
  email: "",
  photos: [],
  photoUrls: [],
  uploadedImages: [],
  uploadSessionId: crypto.randomUUID(),
  rushDelivery: false,
};

export const occasionOptions = [
  { value: "proposal", label: "Proposal", icon: "💍" },
  { value: "wedding", label: "Wedding", icon: "💒" },
  { value: "anniversary", label: "Anniversary", icon: "💕" },
  { value: "quinceanera", label: "Quinceañera", icon: "👑" },
  { value: "graduation", label: "Graduation", icon: "🎓" },
  { value: "birthday", label: "Birthday", icon: "🎂" },
  { value: "new_baby", label: "New Baby", icon: "👶" },
  { value: "adoption", label: "Adoption", icon: "🏠" },
  { value: "retirement", label: "Retirement", icon: "🎉" },
  { value: "promotion", label: "Promotion", icon: "📈" },
  { value: "personal_milestone", label: "Personal Milestone", icon: "⭐" },
  { value: "memorial", label: "Memorial", icon: "🕯️" },
  { value: "just_because", label: "Just Because", icon: "💝" },
  { value: "other", label: "Other", icon: "✨" },
];

export const recipientOptions = [
  { value: "husband", label: "Husband" },
  { value: "wife", label: "Wife" },
  { value: "boyfriend", label: "Boyfriend" },
  { value: "girlfriend", label: "Girlfriend" },
  { value: "fiance", label: "Fiancé (male)" },
  { value: "fiancee", label: "Fiancée (female)" },
  { value: "partner", label: "Partner" },
  { value: "child", label: "Child" },
  { value: "parent", label: "Parent" },
  { value: "grandparent", label: "Grandparent" },
  { value: "aunt_uncle", label: "Aunt or Uncle" },
  { value: "sibling", label: "Sibling" },
  { value: "family_member", label: "Family Member" },
  { value: "friend", label: "Friend" },
  { value: "teacher", label: "Teacher" },
  { value: "pet", label: "Pet" },
  { value: "self", label: "Self" },
  { value: "other", label: "Other" },
];

export const nameCountOptions = [
  { value: "one", label: "One name" },
  { value: "two", label: "Two names" },
  { value: "three_plus", label: "Three or more names" },
  { value: "group", label: "Group or family name" },
  { value: "other", label: "Other" },
];

export const feelingOptions = [
  { value: "romantic", label: "Romantic" },
  { value: "joyful", label: "Joyful" },
  { value: "emotional", label: "Emotional" },
  { value: "fun", label: "Fun" },
  { value: "reflective", label: "Reflective" },
  { value: "uplifting", label: "Uplifting" },
  { value: "calm", label: "Calm" },
  { value: "hopeful", label: "Hopeful" },
  { value: "proud", label: "Proud" },
  { value: "other", label: "Other" },
];

export const songStyleOptions = [
  { value: "pop", label: "Pop" },
  { value: "acoustic_pop", label: "Acoustic Pop" },
  { value: "contemporary_ballad", label: "Contemporary Ballad" },
  { value: "singer_songwriter", label: "Singer Songwriter" },
  { value: "soft_rock", label: "Soft Rock" },
  { value: "country", label: "Country" },
  { value: "country_pop", label: "Country Pop" },
  { value: "folk", label: "Folk" },
  { value: "indie_folk", label: "Indie Folk" },
  { value: "rnb", label: "R&B" },
  { value: "soul", label: "Soul" },
  { value: "neo_soul", label: "Neo Soul" },
  { value: "gospel", label: "Gospel" },
  { value: "worship", label: "Worship" },
  { value: "latin_pop", label: "Latin Pop" },
  { value: "latin_cumbia", label: "Latin Cumbia" },
  { value: "latin_ballad", label: "Latin Ballad" },
  { value: "bachata", label: "Bachata" },
  { value: "salsa", label: "Salsa" },
  { value: "regional_mexican", label: "Regional Mexican" },
  { value: "hip_hop", label: "Hip Hop" },
  { value: "chill_hop", label: "Chill Hop" },
  { value: "rnb_pop", label: "R&B Pop" },
  
  { value: "childrens_song", label: "Children's Song" },
  { value: "lullaby", label: "Lullaby" },
  { value: "indie_pop", label: "Indie Pop" },
  { value: "dream_pop", label: "Dream Pop" },
  { value: "lo_fi", label: "Lo-Fi" },
  { value: "cinematic", label: "Cinematic" },
  { value: "other", label: "Other" },
];

export const tempoOptions = [
  { value: "slow", label: "Slow" },
  { value: "medium", label: "Medium" },
  { value: "upbeat", label: "Upbeat" },
  { value: "very_upbeat", label: "Very Upbeat" },
  { value: "builds_slowly", label: "Builds Slowly" },
  { value: "other", label: "Other" },
];

export const languageOptions = [
  { value: "english", label: "English" },
  { value: "spanish", label: "Spanish" },
  { value: "english_spanish", label: "English and Spanish" },
];

// Step configuration for each plan
export const essentialsSteps = [
  "occasion",
  "recipient",
  "nameCount",
  "names",
  "pronunciation",
  "feeling",
  "style",
  "tempo",
  "language",
  "story",
  "review",
] as const;

export const premiumSteps = [
  "occasion",
  "videoDate",
  "recipient",
  "nameCount",
  "names",
  "pronunciation",
  "feeling",
  "style",
  "tempo",
  "language",
  "story",
  "photos",
  "review",
] as const;

export type EssentialsStep = typeof essentialsSteps[number];
export type PremiumStep = typeof premiumSteps[number];
export type FormStep = EssentialsStep | PremiumStep;
